








































































































































































































import {
  computed,
  defineComponent,
  ref,
  toRefs,
  useFetch
} from '@nuxtjs/composition-api'
import { faClock } from '@fortawesome/free-regular-svg-icons/faClock'
import { faBan } from '@fortawesome/free-solid-svg-icons'
import { format, timeDistanceFromNow } from '~/utils/date'
import { differenceInMinutes } from 'date-fns'
import { USER_NS, UserState } from '~/store/modules/shared/user/state'
import { useNamespacedStore } from '~/compositions/store'
import { useDeps } from '~/compositions/dependency-container'
import AutoTouchService from '~/services/auto-touch/AutoTouchService'
import { handleError } from '~/utils/http'
import { useSnackbar } from '~/compositions/snackbar'
import { useLogger } from '~/compositions/logger'
import { useI18n } from '~/compositions/i18n'
import {
  AutoTouchOneClick,
  AutoTouchOneClickData
} from '~/models/auto-touch/types'
import { ciAutoTouch } from '~/icons/source/solid/auto-touch'
import { capitalizeAllWords } from '~/utils/string'
import StripeService from '~/services/logistics/StripeService'
import CBadge from '~/components/shared/configurable/badge/CBadge.vue'
import { SIMPLE_DATE_FORMAT } from '~/constants/date'
import CPrice from '~/components/shared/configurable/CPrice.vue'
import CConfirmationModal from '~/components/shared/configurable/modal/CConfirmationModal.vue'
import { defineComponentTranslations } from '~/utils/i18n'
import { AxiosError } from 'axios'

export default defineComponent({
  components: { CPrice, CBadge, CConfirmationModal },
  props: {
    classifiedId: {
      type: Number,
      required: true
    },
    square: {
      type: Boolean,
      required: false,
      default: false
    },
    inPromo: {
      type: Boolean,
      required: false
    }
  },
  setup(props) {
    const { classifiedId } = toRefs(props)
    const { getters } = useNamespacedStore<UserState>(USER_NS)
    const paidCredits = computed(() => {
      return getters('paidCredits')
    })

    const disabled = ref(false)
    const isConfirmationModalVisible = ref(false)
    const snackbar = useSnackbar()
    const logger = useLogger()
    const { t } = useI18n()
    const [autoTouchService, stripeService] = useDeps(
      AutoTouchService,
      StripeService
    )

    const loading = ref(false)

    const autoTouchOneClick = ref<AutoTouchOneClick | null>(null)

    useFetch(async () => await fetchData())
    const fetchData = async () => {
      loading.value = true
      try {
        autoTouchOneClick.value = await autoTouchService.fetchOneClickAutoTouchData(
          classifiedId.value
        )
      } catch (error) {
        handleError(error, snackbar, logger, t)
      } finally {
        loading.value = false
      }
    }

    const isActive = computed<boolean>(() =>
      Boolean(autoTouchOneClick.value?.autoTouch?.isActive)
    )
    const autoTouchData = computed<AutoTouchOneClickData | null>(() => {
      // shortcut
      return autoTouchOneClick.value?.autoTouch || null
    })

    const expiresIn = computed(() => {
      if (isActive.value && autoTouchData.value?.expires) {
        return timeDistanceFromNow(autoTouchData.value.expires, {
          addSuffix: false
        })
      }
      return null
    })

    const expirationDate = computed(() => {
      if (isActive.value && autoTouchData.value?.expires) {
        return format(autoTouchData.value.expires, SIMPLE_DATE_FORMAT)
      }
      return null
    })

    const expirePercentage = computed(() => {
      if (isActive.value && autoTouchData.value?.expires) {
        const days = 30
        const totalMinutes = days * 1440 // 1440 is minutes in a day
        const minutesTillEnd = differenceInMinutes(
          new Date(autoTouchData.value.expires),
          Date.now()
        )
        const purePercentage = 100 - (minutesTillEnd / totalMinutes) * 100
        if (purePercentage >= 3) {
          return purePercentage + '%'
        }
        // else return a hardcoded value in order to be visually ok
        return '2%'
      }
      return '100%'
    })

    const onClick = async (e: Event) => {
      if (process.client && !isActive.value && !loading.value) {
        e.preventDefault()
        e.stopPropagation()

        loading.value = true

        try {
          const {
            url,
            session
          } = await autoTouchService.getOneClickStripeDetails(
            classifiedId.value
          )

          stripeService.setSessionCookie(session)
          loading.value = false
          if (window.location.pathname.match(/iframe/)) {
            window.parent.location.href = url
          } else {
            window.location.href = url
          }
        } catch (error) {
          handleError(error as AxiosError, snackbar, logger, t)
        } finally {
          loading.value = false
        }
      }
    }

    async function cancelOneClickAutotouch() {
      loading.value = true
      try {
        const { message } = await autoTouchService.cancelOneClickAutotouch(
          classifiedId.value
        )
        snackbar.success(message)
      } catch (error) {
        handleError(error, snackbar, logger, t)
      } finally {
        loading.value = false
      }
    }

    return {
      paidCredits,
      ciAutoTouch,
      faClock,
      faBan,
      autoTouchData,
      expiresIn,
      expirePercentage,
      disabled,
      autoTouchOneClick,
      capitalizeAllWords,
      onClick,
      loading,
      expirationDate,
      isActive,
      cancelOneClickAutotouch,
      isConfirmationModalVisible
    }
  },
  i18n: defineComponentTranslations({
    cancel_autotouch_confrimation_msg: {
      en: 'Are you sure you want to cancel one-click auto renewal?',
      el:
        'Είσαι σίγουρος οτι θέλεις να ακυρώσεις την αυτόματη ανανέωση one-click;'
    }
  })
})
